/* eslint-disable no-param-reassign */

import React, { useState, useEffect } from 'react';
import {
  chain,
  filter,
  flatten,
  map,
  mean,
  round,
  set,
  startCase,
  sum,
  uniq,
} from 'lodash';
import {
  addYears,
  addMonths,
  addWeeks,
  addDays,
  compareAsc,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { Chart } from '@cordata/buffet';

const getChartDateRange = (chart, filters) => {
  const dateRangeFilters = filters.filter(f => f.type === 'date range');

  let startDate;
  let endDate;

  if (dateRangeFilters.length) {
    // if date range filters exist, use the latest chosen start date and the earliest end date

    startDate = dateRangeFilters
      .map(f => f.startDate)
      .sort(compareAsc)
      .pop();

    endDate = dateRangeFilters
      .map(f => f.endDate)
      .sort(compareAsc)
      .shift();
  } else {
    // if no date range filters exist, use the earliest date from the data as
    // the start and latest as the end

    startDate = chart.data
      .sort((a, b) => compareAsc(a[chart.xAxisColumn.value], b[chart.xAxisColumn.value]))
      .shift()[chart.xAxisColumn.value];

    endDate = chart.data
      .sort((a, b) => compareAsc(a[chart.xAxisColumn.value], b[chart.xAxisColumn.value]))
      .pop()[chart.xAxisColumn.value];
  }

  return { startDate, endDate };
};

const isNull = (value) =>
  value === null ||
  value === 'null' ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '');

const chartifyData = (chart, filters = []) => {
  let labels = [];
  let data = [];
  let chartData = {};
  let datasets = [];

  if (chart.showNulls || chart.showNulls === undefined) {
    // do this when the option is undefined so that it happens on existing
    // reports that don't have the option set

    chart.data = chart.data.map(d => {
      const columnName = chart.columnName || (chart.yAxisColumn && chart.yAxisColumn[0].value);
      const value = d[columnName];

      if (isNull(value)) {
        d[columnName] = chart.nullText || 'Unknown';
      }

      return d;
    });
  } else {
    chart.data = chart.data.filter(d => {
      const columnName = chart.columnName || (chart.yAxisColumn && chart.yAxisColumn[0].value);
      const value = d[columnName];

      return !isNull(value);
    });
  }

  if (chart.type === 'line' || (chart.type === 'bar' && chart.xAxisColumn)) {
    chartData = chart.yAxisColumn.map(yac =>
      chain(chart.data)
        .groupBy((row) => {
          if (chart.isTimeSeries) {
            switch (chart.timeUnit.value) {
              case 'year':
                return format(row[chart.xAxisColumn.value], 'YYYY');

              case 'month':
                return format(row[chart.xAxisColumn.value], 'MMM YYYY');

              case 'week':
                return format(startOfWeek(row[chart.xAxisColumn.value]), 'MM/DD/YYYY');

              default:
                return format(row[chart.xAxisColumn.value], 'MM/DD/YYYY');
            }
          } else {
            return row[chart.xAxisColumn.value];
          }
        })
        .mapValues((v) => {
          if (chart.shouldAggregateData || chart.aggregationType) {
            const nonNullValues = filter(v, vv => vv[yac.value] !== null);

            if (chart.aggregationType === 'count') {
              return nonNullValues.length;
            }

            if (chart.aggregationType === 'unique-count') {
              return uniq(nonNullValues.map((item) => item[yac.value])).length;
            }

            const floatValues = map(nonNullValues, vv => parseFloat(vv[yac.value]) || 0);

            if (chart.aggregationType === 'average') {
              return round(mean(floatValues), 2);
            }

            return sum(floatValues);
          }

          return chain(v)
            .groupBy(yac.value)
            .mapValues(vv => vv.length)
            .value();
        })
        .value());
  } else {
    [labels, data] = chain(chart.data)
      .groupBy(chart.columnName || (chart.yAxisColumn && chart.yAxisColumn[0].value))
      .mapValues(v => v.length)
      .toPairs()
      .unzip()
      .value();

    datasets = [{
      data: data || [],
      label: chart.label,
    }];
  }

  if (chart.type === 'line' || (chart.type === 'bar' && chart.xAxisColumn)) {
    if (chart.shouldAggregateData || chart.aggregationType) {
      chart.yAxisColumn.forEach((yac) => datasets.push({
        label: startCase(yac.value),
        data: [],
      }));
    } else {
      const uniqValues = uniq(flatten(chart.yAxisColumn.map(yac =>
        chart.data.map(d => d[yac.value]))));

      uniqValues.forEach((uv) => datasets.push({
        label: uv,
        data: [],
      }));
    }

    const loadDatasets = (xValue) => {
      chartData.forEach((cd, cdi) => {
        if (chart.shouldAggregateData || chart.aggregationType) {
          datasets[cdi].data.push(cd[xValue] || 0);
        } else {
          datasets.forEach((ds) => {
            ds.data.push((cd[xValue] || {})[ds.label] || 0);
          });
        }
      });
    };

    if (chart.isTimeSeries) {
      const { startDate, endDate } = getChartDateRange(chart, filters);

      switch (chart.timeUnit.value) {
        case 'year':
          for (
            let year = startOfYear(startDate);
            compareAsc(year, endDate) < 1;
            year = addYears(year, 1)
          ) {
            const xValue = format(year, 'YYYY');

            labels.push(chart.xTickFormat ? format(year, chart.xTickFormat.value) : xValue);

            loadDatasets(xValue);
          }

          break;

        case 'month':
          for (
            let month = startOfMonth(startDate);
            compareAsc(month, endDate) < 1;
            month = addMonths(month, 1)
          ) {
            const xValue = format(month, 'MMM YYYY');

            labels.push(chart.xTickFormat ? format(month, chart.xTickFormat.value) : xValue);

            loadDatasets(xValue);
          }

          break;

        case 'week':
          for (
            let week = startOfWeek(startDate);
            compareAsc(week, endDate) < 1;
            week = addWeeks(week, 1)
          ) {
            const xValue = format(week, 'MM/DD/YYYY');

            labels.push(chart.xTickFormat ? format(week, chart.xTickFormat.value) : `Week of ${xValue}`);

            loadDatasets(xValue);
          }

          break;

        default:
          for (
            let day = startOfDay(startDate);
            compareAsc(day, endDate) < 1;
            day = addDays(day, 1)
          ) {
            const xValue = format(day, 'MM/DD/YYYY');

            labels.push(chart.xTickFormat ? format(day, chart.xTickFormat.value) : xValue);

            loadDatasets(xValue);
          }

          break;
      }
    } else {
      const xValues = uniq(chart.data.map(d => d[chart.xAxisColumn.value]));

      xValues.forEach((xValue) => {
        labels.push(xValue);

        loadDatasets(xValue);
      });
    }
  }

  const options = {
    legend: {
      display: chart.shouldShowLegend || false,
      position: chart.legendPosition || 'bottom',
    },
    scales: {
      yAxes: [{
        height: chart.height,
      }],
    },
    plugins: {
      datalabels: {
        display: chart.displayDataLabels === undefined ? true : chart.displayDataLabels,
        formatter: (value, context) => {
          if (
            chart.type === 'bar' &&
            chart.isStacked &&
            chart.showNulls === false && // undefined is considered true for this option
            value === 0
          ) {
            return '';
          }

          if (chart.dataLabelValue) {
            if (chart.dataLabelValue === 'percentage') {
              const total = sum(context.dataset.data);
              return `${round((value / total) * 100, 2)}%`;
            }

            return value;
          }

          if (chart.type === 'pie' || chart.type === 'doughnut') {
            const total = sum(context.dataset.data);
            return `${round((value / total) * 100, 2)}%`;
          }

          return value;
        },
      },
    },
  };

  if (['pie', 'doughnut'].includes(chart.type)) {
    set(options, 'scales.xAxes[0].display', false);
    set(options, 'scales.yAxes[0].display', false);
  } else {
    if (chart.yTickMin) set(options, 'scales.yAxes[0].ticks.min', chart.yTickMin);
    if (chart.yTickMax) set(options, 'scales.yAxes[0].ticks.max', chart.yTickMax);

    if (chart.yTickFormat) {
      if (chart.yTickFormat === 'integer') {
        set(options, 'scales.yAxes[0].ticks.callback', value => (parseInt(value, 10) === value ? value : undefined));
      } else if (chart.yTickFormat === 'percent') {
        set(options, 'scales.yAxes[0].ticks.callback', value => `${round(value, 2)}%`);
      } else if (chart.yTickFormat === 'decimal') {
        set(options, 'scales.yAxes[0].ticks.callback', value => round(value, 2));
      }
    }

    if (chart.type === 'bar' && chart.isStacked) {
      set(options, 'scales.xAxes[0].stacked', true);
      set(options, 'scales.yAxes[0].stacked', true);
    }

    if (chart.type === 'line' && chart.isFilledLine) {
      set(options, 'fill', true);
    }
  }

  return {
    height: chart.height / 5,
    type: chart.type,
    data: {
      datasets,
      labels,
    },
    options,
  };
};

const ReportChart = ({
  title,
  ...props
}) => {
  const [chartifiedData, setChartifiedData] = useState({
    data: { datasets: [], labels: [] },
    options: { legend: {}, plugins: {} },
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (props.data) {
      setLoaded(false);

      setChartifiedData(chartifyData(props, props.filters || []));
    }
  }, [props.data, props.filters]);

  useEffect(() => {
    setLoaded(true);
  }, [chartifiedData]);

  return (<>
    {title && (
      <h2 className="m-3">{title}</h2>
    )}

    {loaded && (
      <Chart
        {...props}
        {...chartifiedData}
        legend={chartifiedData.options.legend}
        plugins={chartifiedData.options.plugins}
      />
    )}
  </>);
};

export default ReportChart;
